import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useEventDemo } from '../hooks/useEventDemo';
import { eventFormSchema, type EventFormValues } from '../schema/showcaseSchema';

const DEFAULT_PAYLOAD = '{"hello":"world","count":42}';

export function EventPanel() {
  const { t } = useTranslation();
  const { mutate, isPending, data, isSuccess, isError, error, reset } = useEventDemo();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<EventFormValues>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: { payloadJson: DEFAULT_PAYLOAD },
  });

  function onSubmit(values: EventFormValues) {
    let payload: Record<string, unknown>;
    try {
      payload = JSON.parse(values.payloadJson) as Record<string, unknown>;
    } catch {
      return;
    }
    mutate({ payload });
  }

  return (
    <section aria-labelledby="event-panel-title">
      <h2
        id="event-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-4)',
          marginTop: 0,
        }}
      >
        {t('showcase.event.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-6)' }}>
        C008 — EventPublisher.publishImmediate() · ShowcaseDemoEvent · ShowcaseEventListener
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label
            htmlFor="event-payload"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {t('showcase.event.payload')}
          </label>
          <textarea
            id="event-payload"
            rows={4}
            aria-label={t('showcase.event.payload')}
            aria-invalid={!!errors.payloadJson}
            aria-describedby={errors.payloadJson ? 'event-payload-error' : undefined}
            {...register('payloadJson')}
            disabled={isPending}
            style={{
              width: '100%',
              padding: 'var(--spacing-3)',
              border: `1px solid ${errors.payloadJson ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-family-mono)',
              background: isPending ? 'var(--color-surface-muted)' : 'var(--color-surface-background)',
              color: 'var(--color-text-primary)',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
          {errors.payloadJson && (
            <p
              id="event-payload-error"
              role="alert"
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', marginTop: 'var(--spacing-1)' }}
            >
              {errors.payloadJson.message}
            </p>
          )}
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-6)' }}>
          <button
            type="submit"
            disabled={isPending || !isValid}
            aria-busy={isPending}
            style={{
              padding: 'var(--spacing-3) var(--spacing-6)',
              background: isPending ? 'var(--color-text-muted)' : 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            {isPending && (
              <span
                aria-hidden="true"
                style={{
                  width: 14,
                  height: 14,
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'var(--color-text-inverse)',
                  borderRadius: '50%',
                  display: 'inline-block',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            )}
            {isPending ? t('showcase.event.loading') : t('showcase.event.publish')}
          </button>
          {isSuccess && (
            <button
              type="button"
              onClick={() => reset()}
              style={{
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'transparent',
                color: 'var(--color-text-secondary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('showcase.common.reset')}
            </button>
          )}
        </div>
      </form>

      {/* Result card */}
      {isSuccess && data && (
        <div
          role="region"
          aria-label={t('showcase.event.resultLabel')}
          style={{
            border: '1px solid var(--color-success)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <div style={{ marginBottom: 'var(--spacing-3)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span style={{ color: 'var(--color-success)', fontSize: 'var(--font-size-lg)' }} aria-hidden="true">✓</span>
            <strong style={{ color: 'var(--color-success)' }}>{t('showcase.event.published')}</strong>
          </div>

          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--spacing-1) var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}>
            <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.event.result.eventId')}</dt>
            <dd style={{ margin: 0 }}>
              <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', wordBreak: 'break-all' }}>
                {data.eventId}
              </code>
            </dd>
            <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.event.result.source')}</dt>
            <dd style={{ margin: 0 }}>
              <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                {data.source}
              </code>
            </dd>
            <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.event.result.listenerCount')}</dt>
            <dd style={{ margin: 0 }}>
              <strong style={{ color: 'var(--color-brand-primary)' }}>listenerCount: {data.listenerCount}</strong>
            </dd>
            <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.event.result.echo')}</dt>
            <dd style={{ margin: 0 }}>
              <pre
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  background: 'var(--color-surface-elevated)',
                  padding: 'var(--spacing-2)',
                  borderRadius: 'var(--radius-sm)',
                  overflow: 'auto',
                  maxHeight: 120,
                  margin: 0,
                  color: 'var(--color-text-primary)',
                }}
              >
                {JSON.stringify(data.echoedPayload, null, 2)}
              </pre>
            </dd>
            <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.event.result.occurredAt')}</dt>
            <dd style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-xs)' }}>
              {data.occurredAt}
            </dd>
          </dl>
        </div>
      )}

      {/* Error */}
      {isError && error && (
        <div
          role="alert"
          style={{
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            color: 'var(--color-danger)',
            fontSize: 'var(--font-size-sm)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <strong>{error.code}</strong>: {error.message}
        </div>
      )}

      {/* Learning message */}
      <div
        style={{
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <span aria-hidden="true" style={{ marginRight: 'var(--spacing-2)' }}>📚</span>
        <strong>{t('showcase.panel.learning')}</strong> {t('showcase.event.learning')}
      </div>
    </section>
  );
}
